import { useEffect } from 'react';
import Map from 'ol/Map';
import { Port, useAISMapContext } from './AISMapContext';
import { useCoverage } from './Coverage/CoverageContext';
import { SourceIdentifier } from '@ais-ui/api-spec/coverage';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { useGPSLost } from './GpsLost/GPSLostContext';

interface ListenForLayerClicksProps {
  map: Map;
}

export const ListenForLayerClicks = ({ map }: ListenForLayerClicksProps) => {
  const { setSelectedPorts } = useSelectionState();
  const { setSelectedSourceIdentifiers } = useCoverage();
  const {
    setSelectedGPSLostGridCell,
    setClickCoordinate: setGPSLostClickCoordinate,
  } = useGPSLost();

  useEffect(() => {
    const handleLayerClick = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { properties, isCtrlClick, coordinate, position } =
        customEvent.detail;

      if (properties?.type === 'port') {
        // We add the port to the list of selected ports if CTRL is clicked
        if (isCtrlClick) {
          setSelectedPorts((prevPorts) => {
            const selectedPort: Port = {
              id: properties.id,
              name: properties.name,
              country: properties.country,
              locode: properties.locode,
              geometry: properties.geometry,
            };

            return [...prevPorts, selectedPort];
          });
        } else {
          setSelectedPorts([
            {
              id: properties.id,
              name: properties.name,
              country: properties.country,
              locode: properties.locode,
              geometry: properties.geometry,
            },
          ]);
        }
      } else if (properties?.type === 'source-identifier') {
        if (isCtrlClick) {
          setSelectedSourceIdentifiers((prevSourceIdentifiers) => {
            const sourceIdentifier: SourceIdentifier = {
              id: parseInt(properties.id),
              countryMid: properties.countryMid,
              pos: properties.pos,
              regionId: properties.regionId,
              pssMmsi: properties.pssMmsi,
              sourceTag: properties.sourceTag,
              covEnabled: properties.covEnabled,
              createdTimeUtc: properties.createdTimeUtc,
              uuid: properties.uuid,
            };

            return [...prevSourceIdentifiers, sourceIdentifier];
          });
        } else {
          setSelectedSourceIdentifiers([
            {
              id: parseInt(properties.id),
              countryMid: properties.countryMid,
              pos: properties.pos,
              regionId: properties.regionId,
              pssMmsi: properties.pssMmsi,
              sourceTag: properties.sourceTag,
              covEnabled: properties.covEnabled,
              createdTimeUtc: properties.createdTimeUtc,
              uuid: properties.uuid,
            },
          ]);
        }
      } else if (properties?.type === 'gps-lost') {
        setSelectedGPSLostGridCell({
          id: properties.id,
          configurationId: properties.configurationId,
          instanceId: properties.instanceId,
          mmsis: properties.mmsi_list,
          index: properties.index,
          gridLayerId: properties.gridLayerId,
        });
        setGPSLostClickCoordinate(coordinate);
      } else {
        console.log('Feature clicked:', properties?.name);
      }
    };

    const mapElement = map.getTargetElement(); // Get the DOM element associated with the map

    if (mapElement) {
      mapElement.addEventListener('layer-click', handleLayerClick);
    }

    return () => {
      if (mapElement) {
        mapElement.removeEventListener('layer-click', handleLayerClick);
      }
    };
  }, [map]);

  return null;
};
